// src/lib/quickCheckDraft.ts
// Schnell-Check (/check): Entwurf + anonyme ID in localStorage (nicht
// sessionStorage) -- muss die Registrierung UND die E-Mail-Bestaetigung
// ueberstehen, die je nach Clerk-Konfiguration/In-App-Browser in einem neuen
// Tab/Fenster abgeschlossen wird; sessionStorage waere dann schon weg.
//
// Bewusst KEINE personenbezogenen Daten im Entwurf: nur Objektzahlen + PLZ
// (die PLZ beschreibt das Objekt, nicht die Person). Kein Name, keine
// Adresse, keine E-Mail. TTL 24h, danach wird ein liegen gebliebener Entwurf
// beim naechsten Lesen verworfen statt ewig gueltig zu bleiben.

/** Objektart, erfasst in Schritt 0 (Step0Objektart.tsx) -- dieselbe Konvention wie
 *  AnalyzerType (usePortfolio.ts) / AnalyzerTypeId (etwSnapshot.ts etc.). Default "etw"
 *  fuer Rueckwaertskompatibilitaet mit Entwuerfen von vor Schritt 0. */
export type QuickCheckObjektart = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

export type QuickCheckInput = {
  objektart: QuickCheckObjektart;
  kaufpreis: number;
  flaecheM2: number;
  plz: string;
  /** Manuell gewaehlt/korrigiert (siehe Check.tsx) -- ueberschreibt die Auto-Erkennung aus der PLZ. */
  bundeslandOverride: string | null;
  /** null = noch nicht gewaehlt (Dominiks Vorgabe 12.10.2026, Punkt 3: keine Vorauswahl,
   *  Pflichtfeld wie Bundesland -- "Weiter" erst nach aktiver Wahl). */
  provisionJa: boolean | null;
  /** Paket 28: eigene Höhe der Käuferprovision (Anteil, z. B. 0.0238). null = üblich 3,57 %
   *  (NK_DEFAULT_PCTS.makler). Nur wirksam bei provisionJa === true. */
  provisionPct: number | null;
  /** Paket 28: gewählte Ortschaft, wenn die PLZ mehrere Orte hat (nur Anzeige/Name, kein Score). */
  ort: string | null;
  kaltmieteMonat: number;
  hausgeldMonat: number;
  /** Anteil des Hausgeldes, den der Eigentuemer nicht umlegen kann (0..1), editierbar, Default 0.35.
   *  Nur fuer objektart "etw" -- die 4 neuen Typen nutzen stattdessen bewirtschaftungPctOverride. */
  hausgeldSelbstAnteilPct: number;
  /** Nur fuer die 4 neuen Typen (nicht ETW): optionale abweichende Bewirtschaftungskosten (Anteil
   *  der Kaltmiete, 0..1). null = Typ-Pauschale verwenden (siehe dealScoreConfig.ts). */
  bewirtschaftungPctOverride: number | null;
  /**
   * Deal-Score v2 Vermietungsstatus. Default "vollstaendig". Bei "vollstaendig" ist
   * kaltmieteMonat die (einzige) Kaltmiete. Bei "nein" ist kaltmieteMonat die erwartete
   * Miete (Leerstand-Obergrenze greift). Bei "teilweise" (nur mfh/gewerbe/mixeduse -- ETW
   * hat nur eine Einheit, EFH ebenfalls ohne Mehrfacheinheiten-Konzept, siehe
   * Step2Miete.tsx) ist kaltmieteMonat die AKTUELLE (Ist-)Miete, kaltmieteMonatSoll die
   * Miete bei Vollvermietung (Dominiks OK vom 12.10.2026, Nachtrag zu Punkt 2) -- keine
   * Obergrenze, der Score faellt organisch niedriger aus.
   */
  vermietet: "vollstaendig" | "teilweise" | "nein";
  /** Nur bei vermietet="teilweise": Miete bei Vollvermietung (€/Monat). Sonst null. */
  kaltmieteMonatSoll: number | null;
  eigenkapital: number;
  zinsPct: number;
  tilgungPct: number;
};

export type QuickCheckDraft = {
  v: 1;
  anonymousId: string;
  /** Eine ID pro Durchlauf, ueberlebt die Registrierung -- fuer den /check-Tracking-
   *  Funnel (quickCheckFunnel.ts), damit quickcheck_started/quickcheck_step_completed
   *  (anonym, vor Login) und result_viewed (nach Login) dieselbe quickcheck_id tragen.
   *  Nicht die anonymousId: die ist geraete-/browserweit, quickCheckId ist pro Durchlauf
   *  (Dominiks Vorgabe vom 12.10.2026, Punkt 4). */
  quickCheckId: string;
  expiresAt: number;
  input: QuickCheckInput;
  /** Paket 30: Ergebnis konnte wegen der FREE-Grenze nicht gespeichert werden. Ein geparkter
   *  Entwurf löst KEINE automatische Weiterleitung nach /check aus (App.tsx), sonst kommt der
   *  Nutzer nie zu „Meine Objekte“, um ein altes Objekt zu löschen. */
  geparkt?: boolean;
};

const DRAFT_KEY = "pp_quickcheck_draft";
const ANON_ID_KEY = "pp_anon_id";
const RESULT_KEY = "pp_quickcheck_result_deal_id";
const TTL_MS = 24 * 60 * 60 * 1000;

function hasStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

/** Persistente anonyme ID, ueberlebt den gesamten Schnell-Check + Registrierung. */
export function getOrCreateAnonymousId(): string {
  if (!hasStorage()) return "";
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(ANON_ID_KEY, id);
    return id;
  } catch {
    return "";
  }
}

/** Nach dem Claim (siehe Check.tsx) rotiert: ein geteiltes Geraet soll nicht an der alten ID weiterhaengen. */
export function rotateAnonymousId(): void {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(ANON_ID_KEY);
  } catch {
    // ignorieren
  }
}

export function saveQuickCheckDraft(input: QuickCheckInput, quickCheckId: string): void {
  if (!hasStorage()) return;
  try {
    const draft: QuickCheckDraft = {
      v: 1,
      anonymousId: getOrCreateAnonymousId(),
      quickCheckId,
      expiresAt: Date.now() + TTL_MS,
      input,
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // localStorage voll/blockiert (z. B. privater Modus) -- Entwurf geht verloren, kein Crash.
  }
}

export const VALID_OBJEKTARTEN: QuickCheckObjektart[] = ["etw", "mfh", "efh", "gewerbe", "mixeduse"];

/** Fuellt Felder auf, die ein Entwurf von vor Schritt 0 (Objektart) noch nicht kennt --
 *  verhaelt sich dann exakt wie der bisherige, reine ETW-Ablauf. */
function backfillInput(input: Partial<QuickCheckInput>): QuickCheckInput {
  return {
    objektart: VALID_OBJEKTARTEN.includes(input.objektart as QuickCheckObjektart) ? (input.objektart as QuickCheckObjektart) : "etw",
    kaufpreis: input.kaufpreis ?? 0,
    flaecheM2: input.flaecheM2 ?? 0,
    plz: input.plz ?? "",
    bundeslandOverride: input.bundeslandOverride ?? null,
    // Alte Entwuerfe (vor 12.10.2026, Punkt 3) kannten nur true/false -- bleibt erhalten.
    // Fehlt das Feld (undefined), jetzt null statt eines stillen false-Defaults (sonst
    // koennte ein alter, eigentlich noch unvollstaendiger Entwurf faelschlich als "Nein"
    // durchgehen) -- bewusst NICHT per "??", das wuerde ein gespeichertes explizites null
    // ebenfalls auf false zwingen.
    provisionJa: typeof input.provisionJa === "boolean" ? input.provisionJa : null,
    provisionPct: typeof input.provisionPct === "number" && Number.isFinite(input.provisionPct) && input.provisionPct > 0 && input.provisionPct <= 0.1 ? input.provisionPct : null,
    ort: typeof input.ort === "string" && input.ort.trim() ? input.ort.trim().slice(0, 80) : null,
    kaltmieteMonat: input.kaltmieteMonat ?? 0,
    hausgeldMonat: input.hausgeldMonat ?? 0,
    hausgeldSelbstAnteilPct: input.hausgeldSelbstAnteilPct ?? 0.35,
    bewirtschaftungPctOverride: input.bewirtschaftungPctOverride ?? null,
    vermietet: input.vermietet === "teilweise" || input.vermietet === "nein" ? input.vermietet : "vollstaendig",
    // Alte Entwuerfe ohne das Feld (vor dem 12.10.2026): wenn "teilweise", galt die
    // vorhandene Miete schon als Ist-Miete -- Soll defaultet auf denselben Wert (Dominiks
    // Migrations-Vorgabe), das "Bei Vollvermietung"-Potenzial zeigt dann vorerst denselben
    // Score, bis der Nutzer die Soll-Miete selbst eintraegt.
    kaltmieteMonatSoll: typeof input.kaltmieteMonatSoll === "number" ? input.kaltmieteMonatSoll : (input.kaltmieteMonat ?? 0),
    eigenkapital: input.eigenkapital ?? 0,
    zinsPct: input.zinsPct ?? 0.035,
    tilgungPct: input.tilgungPct ?? 0.02,
  };
}

/** Paket 30: Entwurf als „wegen FREE-Grenze nicht gespeichert“ markieren (bleibt erhalten). */
export function parkeQuickCheckDraft(): void {
  if (!hasStorage()) return;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    const d = JSON.parse(raw);
    if (!d || typeof d !== "object") return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, geparkt: true }));
  } catch {
    /* ohne Speicher nichts zu tun */
  }
}

/** Liest den Entwurf, verwirft ihn automatisch, wenn abgelaufen oder beschaedigt. */
export function loadQuickCheckDraft(): QuickCheckDraft | null {
  if (!hasStorage()) return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<QuickCheckDraft>;
    if (!draft || draft.v !== 1 || typeof draft.expiresAt !== "number" || !draft.input || !draft.anonymousId) {
      return null;
    }
    if (Date.now() > draft.expiresAt) {
      clearQuickCheckDraft();
      return null;
    }
    // Entwuerfe von vor dieser Erweiterung (12.10.2026) kennen quickCheckId noch nicht --
    // frisch generieren statt den sonst gueltigen Entwurf deswegen zu verwerfen (anders als
    // anonymousId/v oben ist das hier keine Pflichtvoraussetzung fuer "Entwurf gueltig").
    return {
      ...draft,
      quickCheckId: typeof draft.quickCheckId === "string" && draft.quickCheckId ? draft.quickCheckId : crypto.randomUUID(),
      input: backfillInput(draft.input),
      geparkt: draft.geparkt === true,
    } as QuickCheckDraft;
  } catch {
    return null;
  }
}

export function clearQuickCheckDraft(): void {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignorieren
  }
}

// ---------------------------------------------------------------------------
// Zwischenstand des Assistenten (Paket 18, 20.10.2026, Conversion-Audit Punkt 3):
// Bisher wurde erst bei „Analyse abschließen“ gespeichert -- Neu laden, App-Wechsel oder
// Zurück-Taste in Schritt 1–3 löschten alles. Der Zwischenstand liegt bewusst unter einem
// EIGENEN Schlüssel: der fertige Entwurf (oben) löst nach der Registrierung das automatische
// Speichern als Deal aus (Check.tsx, Stage "saving"; App.tsx leitet eingeloggte Nutzer mit
// fertigem Entwurf nach /check) -- ein halb ausgefüllter Assistent darf das nie.
// ---------------------------------------------------------------------------

export type QuickCheckZwischenstand = {
  input: QuickCheckInput;
  /** 1–3 (Schritt 0 = Objektart wählen braucht keinen Zwischenstand). */
  step: number;
  quickCheckId: string;
};

const WIP_KEY = "pp_quickcheck_wip";

export function saveQuickCheckZwischenstand(input: QuickCheckInput, step: number, quickCheckId: string): void {
  if (!hasStorage() || step < 1) return;
  try {
    localStorage.setItem(
      WIP_KEY,
      JSON.stringify({ v: 1, expiresAt: Date.now() + TTL_MS, input, step: Math.min(3, Math.round(step)), quickCheckId }),
    );
  } catch {
    // localStorage voll/blockiert -- Zwischenstand geht verloren, kein Crash.
  }
}

/** Liest den Zwischenstand; abgelaufen, beschädigt oder ohne gültigen Schritt → null (und weg). */
export function loadQuickCheckZwischenstand(): QuickCheckZwischenstand | null {
  if (!hasStorage()) return null;
  try {
    const raw = localStorage.getItem(WIP_KEY);
    if (!raw) return null;
    const w = JSON.parse(raw) as { v?: number; expiresAt?: number; input?: Partial<QuickCheckInput>; step?: number; quickCheckId?: string };
    const gueltig =
      w && w.v === 1 && typeof w.expiresAt === "number" && Date.now() <= w.expiresAt && w.input && typeof w.step === "number" && w.step >= 1 && w.step <= 3;
    if (!gueltig) {
      clearQuickCheckZwischenstand();
      return null;
    }
    return {
      input: backfillInput(w.input as Partial<QuickCheckInput>),
      step: w.step as number,
      quickCheckId: typeof w.quickCheckId === "string" && w.quickCheckId ? w.quickCheckId : crypto.randomUUID(),
    };
  } catch {
    return null;
  }
}

export function clearQuickCheckZwischenstand(): void {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(WIP_KEY);
  } catch {
    // ignorieren
  }
}

/** Nach dem Speichern als Deal: dealId merken (sessionStorage reicht -- gilt nur fuer die aktuelle Sitzung,
 *  ein Reload von /check soll das Ergebnis zeigen, ohne erneut zu speichern). */
export function saveQuickCheckResultDealId(dealId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(RESULT_KEY, dealId);
  } catch {
    // ignorieren
  }
}

export function loadQuickCheckResultDealId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(RESULT_KEY);
  } catch {
    return null;
  }
}

/** "Nächstes Objekt prüfen" (/deal/:id, Schritt 8, PHASE-4-BEFUND.md): verwirft die
 *  gemerkte dealId eines vorherigen Durchlaufs, damit /check?objektart=... wirklich
 *  einen frischen Durchlauf startet statt in "loadingExisting" auf den alten,
 *  bereits gezeigten Deal zu laden. */
export function clearQuickCheckResultDealId(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RESULT_KEY);
  } catch {
    // ignorieren
  }
}
